import { isEqual, xor } from "lodash";
import isNullish from "utils/isNullish";
import isNullishOrBlank from "utils/isNullishOrBlank";

import { removeCellEntry, setCellEntry } from "./cellMap";
import { parseValueKey } from "./termRowId";

export const normaliseCellValue = (value) =>
  isNullishOrBlank(value) ? null : value;

const isSameCellValue = (first, second) =>
  Array.isArray(first) && Array.isArray(second)
    ? first.length === second.length && xor(first, second).length === 0
    : isEqual(first, second);

export const applyDraftCell = (
  draft,
  { valueKey, columnId, value, serverValue }
) => {
  const nextValue = normaliseCellValue(value);

  if (isSameCellValue(nextValue, normaliseCellValue(serverValue))) {
    removeCellEntry(draft, valueKey, columnId);
  } else {
    setCellEntry(draft, valueKey, columnId, nextValue);
  }
};

export const columnKey = (column) => column.clientKey ?? column.id;

export const savedCellValue = (row, column) => {
  const savedColumnId = column.clientKey
    ? column.valuesFromColumnId
    : column.id;
  return isNullish(savedColumnId) ? undefined : row?.values?.[savedColumnId];
};

export const draftOrSavedValue = (row, rowDraft, column) => {
  const key = columnKey(column);
  return rowDraft && key in rowDraft
    ? rowDraft[key]
    : savedCellValue(row, column);
};

export const countDraftCells = (draft) =>
  Object.values(draft).reduce(
    (total, rowDraft) => total + Object.keys(rowDraft).length,
    0
  );

const COLUMN_REF_PREFIX = "c:";
const CLIENT_KEY_REF_PREFIX = "k:";

export const isClientKey = (key) =>
  typeof key === "string" && key.startsWith(CLIENT_KEY_REF_PREFIX);

export const toColumnRef = (key) =>
  isClientKey(key) ? key : `${COLUMN_REF_PREFIX}${key}`;

export const toCellKey = (columnRef) => {
  const { columnId, clientKey } = parseColumnRef(columnRef);
  return clientKey ? `${CLIENT_KEY_REF_PREFIX}${clientKey}` : columnId;
};

export const parseColumnRef = (columnRef) => {
  if (columnRef?.startsWith(COLUMN_REF_PREFIX)) {
    return { columnId: Number(columnRef.slice(COLUMN_REF_PREFIX.length)) };
  }
  if (columnRef?.startsWith(CLIENT_KEY_REF_PREFIX)) {
    return { clientKey: columnRef.slice(CLIENT_KEY_REF_PREFIX.length) };
  }
  return {};
};

export const toCellChanges = (draft) =>
  Object.entries(draft).flatMap(([valueKey, rowDraft]) => {
    const { paragraphId, sentenceStart } = parseValueKey(valueKey);
    return Object.entries(rowDraft).map(([key, value]) => ({
      columnRef: toColumnRef(key),
      paragraphId,
      sentenceStart,
      value,
    }));
  });
